import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, MapPin, Phone, Hash, Eye, Calendar, ChevronDown, ChevronRight } from "lucide-react";
import {
  proveedoresAPI,
  type Proveedor,
  type ProveedorPago,
  type ProveedorSaldoHistoryRow,
} from "@/lib/api";
import { movimientoCuentaMatches, movimientosCuentaProveedor } from "./proveedorHistorial";

interface ProveedorPerfilProps {
  proveedor: Proveedor;
}

export function ProveedorPerfil({ proveedor }: ProveedorPerfilProps) {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [expandedMovs, setExpandedMovs] = React.useState<Set<number>>(new Set());
  const itemsPerPage = 10;

  const [allPagos, setAllPagos] = React.useState<ProveedorPago[]>([]);
  const [saldoHistory, setSaldoHistory] = React.useState<ProveedorSaldoHistoryRow[]>([]);
  const [_loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const loadHistorial = async () => {
      setLoading(true);
      try {
        const [pagos, history] = await Promise.all([
          proveedoresAPI.getPagos(proveedor.codigo),
          proveedoresAPI.getSaldoHistory(proveedor.codigo),
        ]);
        setAllPagos(pagos);
        setSaldoHistory(history);
      } catch (error) {
        console.error("Error loading historial:", error);
      } finally {
        setLoading(false);
      }
    };
    if (proveedor.codigo) {
      loadHistorial();
    }
  }, [proveedor.codigo, proveedor.saldo]);

  const pagoPorNumero = React.useMemo(() => {
    const map = new Map<string, ProveedorPago>();
    for (const pago of allPagos) {
      if (pago.numeroPago) map.set(pago.numeroPago, pago);
    }
    return map;
  }, [allPagos]);

  const filteredMovs = movimientosCuentaProveedor(saldoHistory).filter((m) =>
    movimientoCuentaMatches(m, searchQuery),
  );

  const totalPages = Math.ceil(filteredMovs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMovs = filteredMovs.slice(startIndex, startIndex + itemsPerPage);

  const toggleMovExpansion = (movId: number) => {
    setExpandedMovs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(movId)) {
        newSet.delete(movId);
      } else {
        newSet.add(movId);
      }
      return newSet;
    });
  };

  const formatMoney = (n: number) =>
    n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const getMetodoLabel = (tipo: string) => {
    const labels: Record<string, string> = {
      'efectivo': 'Efectivo',
      'transferencia': 'Transferencia',
      'tarjeta_credito': 'Tarjeta de Crédito',
      'tarjeta_debito': 'Tarjeta de Débito',
      'cheque': 'Cheque',
      'dolares': 'Dólares',
      'dólares': 'Dólares'
    };
    return labels[tipo.toLowerCase()] || tipo;
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">{proveedor.nombreRazonSocial}</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Información General */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4" />
              Información General
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Código</label>
              <p className="text-sm mt-0.5">{proveedor.codigo}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">CUIT</label>
              <p className="text-sm mt-0.5">{proveedor.cuit}</p>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Razón Social</label>
              <p className="text-sm mt-0.5">{proveedor.nombreRazonSocial}</p>
            </div>
          </CardContent>
        </Card>

        {/* Ubicación */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4" />
              Ubicación
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Domicilio</label>
              <p className="text-sm mt-0.5">{proveedor.domicilio || "-"}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Localidad</label>
              <p className="text-sm mt-0.5">{proveedor.localidad || "-"}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Provincia</label>
              <p className="text-sm mt-0.5">{proveedor.provincia || "-"}</p>
            </div>
          </CardContent>
        </Card>

        {/* Contacto */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Phone className="h-4 w-4" />
              Contacto
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Persona</label>
              <p className="text-sm mt-0.5">{proveedor.personaContacto || "-"}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Teléfono</label>
              <p className="text-sm mt-0.5">{proveedor.telefono || "-"}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Celular</label>
              <p className="text-sm mt-0.5">{proveedor.celular || "-"}</p>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Email</label>
              <p className="text-sm mt-0.5 truncate">{proveedor.email || "-"}</p>
            </div>
          </CardContent>
        </Card>

        {/* Estado Financiero */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Hash className="h-4 w-4" />
              Estado Financiero
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Saldo</label>
              <p className={`text-2xl font-bold mt-1 ${proveedor.saldo > 0 ? 'text-green-600' : proveedor.saldo < 0 ? 'text-red-600' : 'text-muted-foreground'
                }`}>
                ${Math.abs(proveedor.saldo).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {proveedor.saldo > 0 ? 'Crédito / a favor nuestro' : proveedor.saldo < 0 ? 'Deuda con proveedor' : 'Cuenta saldada'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                {proveedor.saldo < 0 ? 'A pagar' : proveedor.saldo > 0 ? 'Crédito disponible' : 'Estado'}
              </label>
              <div className={`mt-1 p-2 rounded-lg ${proveedor.saldo < 0
                ? 'bg-red-50 dark:bg-red-900/20'
                : proveedor.saldo > 0
                  ? 'bg-green-50 dark:bg-green-900/20'
                  : 'bg-muted'
                }`}>
                <p className={`text-sm font-semibold ${proveedor.saldo < 0 ? 'text-red-700 dark:text-red-400' : proveedor.saldo > 0 ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'
                  }`}>
                  {proveedor.saldo < 0
                    ? 'Pendiente de pago'
                    : proveedor.saldo > 0
                      ? 'Saldo a favor con el proveedor'
                      : 'Sin movimientos'}
                </p>
                {proveedor.saldo > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Se aplicará en próximas compras
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pagos Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Historial de Pagos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Buscar por nro de pedido o pago</label>
              <input
                type="text"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              />
            </div>
          </div>

          {/* Table */}
          {filteredMovs.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No se encontraron movimientos
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-2 text-left w-10"></th>
                      <th className="px-4 py-2 text-left">Fecha</th>
                      <th className="px-4 py-2 text-left">Tipo</th>
                      <th className="px-4 py-2 text-left">Referencia</th>
                      <th className="px-4 py-2 text-left">Descripción</th>
                      <th className="px-4 py-2 text-right">Debe</th>
                      <th className="px-4 py-2 text-right">Haber</th>
                      <th className="px-4 py-2 text-right">Saldo</th>
                      <th className="px-4 py-2 text-left">Factura</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedMovs.map((mov) => {
                      const pago = mov.tipo === 'pago' ? pagoPorNumero.get(mov.referencia) : undefined;
                      const isExpanded = expandedMovs.has(mov.id);
                      return (
                        <React.Fragment key={mov.id}>
                          <tr className="border-b hover:bg-muted/50">
                            <td className="px-4 py-2">
                              {pago?.metodosPago && pago.metodosPago.length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleMovExpansion(mov.id)}
                                  className="h-6 w-6 p-0"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </td>
                            <td className="px-4 py-2">
                              {mov.fecha ? new Date(mov.fecha).toLocaleDateString('es-AR') : '-'}
                            </td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-1 rounded text-xs ${mov.tipo === 'pago' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                                {mov.tipo === 'pago' ? 'Pago' : mov.tipo === 'pedido' ? 'Pedido' : mov.tipo}
                              </span>
                            </td>
                            <td className="px-4 py-2">{mov.referencia || '-'}</td>
                            <td className="px-4 py-2 text-muted-foreground">{mov.descripcion || '-'}</td>
                            <td className="px-4 py-2 text-right text-red-600">
                              {mov.debe > 0 ? `$${formatMoney(mov.debe)}` : ''}
                            </td>
                            <td className="px-4 py-2 text-right text-green-600">
                              {mov.haber > 0 ? `$${formatMoney(mov.haber)}` : ''}
                            </td>
                            <td className="px-4 py-2 text-right">
                              <span className={`font-medium ${mov.saldo > 0 ? 'text-green-600' : mov.saldo < 0 ? 'text-red-600' : ''}`}>
                                ${formatMoney(Math.abs(mov.saldo))}
                                {mov.saldo > 0 && ' (+)'}
                                {mov.saldo < 0 && ' (-)'}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              {pago?.pdfFacturaName ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(pago.pdfFacturaUrl, '_blank')}
                                  className="text-blue-600 hover:text-blue-800 h-auto py-1 px-2"
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  Ver
                                </Button>
                              ) : (
                                <span className="text-muted-foreground text-xs">-</span>
                              )}
                            </td>
                          </tr>
                          {isExpanded && pago?.metodosPago && pago.metodosPago.length > 0 && (
                            <tr className="bg-muted/30">
                              <td colSpan={9} className="px-4 py-3">
                                <div className="ml-8 space-y-2">
                                  <p className="text-xs font-semibold text-muted-foreground mb-2">Métodos de Pago:</p>
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {pago.metodosPago.map((metodo, midx) => (
                                      <div key={midx} className="flex flex-col gap-1 p-2 bg-background rounded border border-border">
                                        <div className="flex items-center justify-between">
                                          <span className="text-sm font-medium">{getMetodoLabel(metodo.tipo)}</span>
                                          <span className="text-sm font-semibold">
                                            ${metodo.monto.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                          <Calendar className="h-3 w-3" />
                                          {pago.fechaPago}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4">
                  <div className="text-sm text-muted-foreground">
                    Mostrando {startIndex + 1} a {Math.min(startIndex + itemsPerPage, filteredMovs.length)} de {filteredMovs.length} movimientos
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Atrás
                    </Button>
                    <div className="flex gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <Button
                          key={page}
                          variant={page === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                          className="w-8"
                        >
                          {page}
                        </Button>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}